import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Loader2, CheckCircle2, Circle, AlertTriangle, ArrowRight,
  Upload, ExternalLink, XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { WebhookStep } from "@/components/laaffic/WebhookStep";

/**
 * Laaffic Integration wizard.
 *
 * Single-page numbered workflow that replaces the previous scattered
 * Laaffic menu (parity / dispositions / backfill / unmapped / creds).
 * Each step is a card; sub-pages still exist and are linked from here
 * for the deeper operations (edit disposition mapping, manage creds).
 *
 * Steps:
 *   1. Credentials + connection test
 *   2. Campaign → Agent Group mapping check
 *   3. Archive existing Excel data  (danger zone)
 *   4. Sanity check (one day)       — Excel-vs-API diff
 *   5. Disposition mapping           — auto-seed + fill canonicals
 *   6. Full pull                     — backfill for the confirmed range
 */
export default function LaafficIntegrationPage() {
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="laaffic-integration-page">
      <div>
        <h1 className="text-2xl font-outfit font-semibold text-slate-900">Laaffic integration</h1>
        <div className="text-sm text-slate-600 mt-1 max-w-3xl">
          One-time setup + go-live workflow. Follow the steps top-to-bottom.
          Each step verifies the previous one before you unlock the next.
        </div>
      </div>

      <Step n={1} title="Credentials & connection test">
        <div className="text-xs text-slate-600 space-y-1">
          <div>Confirm each team has an active Laaffic API key + secret with a passing connection test.</div>
          <Link to="/admin/integrations" className="text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 text-xs">
            Open credentials page <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </Step>

      <Step n={2} title="Campaign → Agent Group mapping">
        <CampaignMappingCheck />
      </Step>

      <Step n={3} title="Archive existing Excel data">
        <ArchiveStep />
      </Step>

      <Step n={4} title="Sanity check — Laaffic Excel vs Laaffic API">
        <SanityCheckStep />
      </Step>

      <Step n={5} title="Disposition map — Laaffic tags → canonical dispositions">
        <div className="text-xs text-slate-600 space-y-1">
          <div>
            Every Laaffic tag needs a canonical disposition (wrong number, no answer, interested, …)
            before it can flow into ops_call_rows. Auto-seed from a recent window then fill the blanks.
          </div>
          <Link to="/admin/laaffic-dispositions" className="text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 text-xs">
            Open disposition mapping <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </Step>

      <Step n={6} title="Full pull — commit Laaffic data to ops_call_rows">
        <div className="text-xs text-slate-600 space-y-1">
          <div>
            Once dispositions are mapped and sanity check passes, run the backfill for the confirmed date range.
            Preview first — no writes until you hit commit.
          </div>
          <div className="flex gap-3">
            <Link to="/admin/laaffic-backfill" className="text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 text-xs">
              Open backfill <ExternalLink className="w-3 h-3" />
            </Link>
            <Link to="/admin/laaffic-unmapped" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-xs">
              Unmapped queue <ExternalLink className="w-3 h-3" />
            </Link>
            <Link to="/admin/laaffic-parity" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-xs">
              Parity report <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </Step>

      <Step n={7} title="Live call push — Laaffic webhook">
        <WebhookStep />
      </Step>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="glass-card p-4" data-testid={`step-${n}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-semibold tabular-nums">
          {n}
        </div>
        <div className="font-outfit font-semibold text-slate-900">{title}</div>
      </div>
      <div className="pl-11">{children}</div>
    </div>
  );
}

/** Step 2: table of campaigns with their Laaffic agent-group id. */
function CampaignMappingCheck() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/campaigns")
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const mapped = rows.filter((c) => c.laaffic_voice_group_id != null);
  const unmapped = rows.filter((c) => c.laaffic_voice_group_id == null);

  return (
    <div className="space-y-2 text-xs">
      {loading ? (
        <div className="text-slate-500 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
      ) : (
        <>
          <div className="flex gap-2 text-[11px]">
            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
              {mapped.length} mapped
            </span>
            {unmapped.length > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                {unmapped.length} unmapped
              </span>
            )}
          </div>
          <div className="glass-card p-0 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="px-3 py-2">Campaign</th>
                  <th className="px-3 py-2">Brand</th>
                  <th className="px-3 py-2">Agent Group ID</th>
                  <th className="px-3 py-2">SIP name</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} className={`border-t border-slate-100 ${c.laaffic_voice_group_id == null ? "bg-amber-50/40" : ""}`}>
                    <td className="px-3 py-1.5 font-mono">{c.name}</td>
                    <td className="px-3 py-1.5 tabular-nums">#{c.brand_id}</td>
                    <td className="px-3 py-1.5 font-mono tabular-nums">{c.laaffic_voice_group_id ?? <span className="text-amber-700">— missing</span>}</td>
                    <td className="px-3 py-1.5 font-mono text-slate-500">{c.laaffic_sip_group_name || "—"}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-4 text-slate-500 text-center">No campaigns yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Link to="/campaigns" className="text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 text-xs">
            Fix mappings on Campaigns page <ArrowRight className="w-3 h-3" />
          </Link>
        </>
      )}
    </div>
  );
}

/** Step 3: archive danger zone. Reused summary — full UI lives on the
 *  existing backfill page for now. Here we just show current status. */
function ArchiveStep() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/integrations/ops-archive-status");
      setStatus(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="text-xs space-y-2">
      {loading && <div className="text-slate-500 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>}
      {status && (
        <>
          <div className="flex gap-2 text-[11px]">
            <span className="px-2 py-0.5 rounded-full bg-slate-100 tabular-nums">
              Current rows: <b>{(status.current_row_count || 0).toLocaleString()}</b>
            </span>
            <span className="px-2 py-0.5 rounded-full bg-slate-100 tabular-nums">
              Archives: <b>{(status.existing_archives || []).length}</b>
            </span>
            {status.current_row_count === 0 && status.existing_archives?.length > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> archive done
              </span>
            )}
          </div>
          <Link to="/admin/laaffic-backfill" className="text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 text-xs">
            Run archive on backfill page <ArrowRight className="w-3 h-3" />
          </Link>
        </>
      )}
    </div>
  );
}

/** Step 4: NEW — sanity check. Upload Laaffic Excel + pick date →
 *  backend pulls the same day from EVERY active team's Laaffic API,
 *  merges the results, and diffs against the Excel. One file → all
 *  teams handled together. */
function SanityCheckStep() {
  const [form, setForm] = useState({
    date: new Date(Date.now() - 864e5).toISOString().slice(0, 10),
  });
  const [file, setFile] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setErr("Pick an Excel file first."); return; }
    setRunning(true); setErr(""); setResult(null); setLogs([]);

    // Stream NDJSON from the backend. Each line is a JSON event —
    // {type: log|team_progress|heartbeat|result|error}. We append log
    // lines to the live viewer as they arrive and stash the ``result``
    // event as the final output.
    try {
      const fd = new FormData();
      fd.append("date", form.date);
      fd.append("file", file);

      const token = localStorage.getItem("od_token") || "";
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const resp = await fetch(
        `${backend}/api/admin/integrations/laaffic/sanity-check`,
        { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok || !resp.body) {
        const txt = await resp.text().catch(() => resp.statusText);
        setErr(`HTTP ${resp.status}: ${txt}`);
        setRunning(false);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n")) !== -1) {
          const line = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 1);
          if (!line) continue;
          let ev;
          try { ev = JSON.parse(line); } catch { continue; }
          if (ev.type === "log") {
            setLogs((prev) => [...prev, `[${ev.ts}] ${ev.msg}`]);
          } else if (ev.type === "heartbeat") {
            /* ignore */
          } else if (ev.type === "error") {
            setErr(ev.detail || "unknown error");
          } else if (ev.type === "result") {
            setResult(ev);
          }
        }
      }
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setRunning(false);
    }
  };

  const verdictBadge = result && (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold inline-flex items-center gap-1 ${
      result.verdict === "pass" ? "bg-emerald-100 text-emerald-800" :
      result.verdict === "warn" ? "bg-amber-100 text-amber-800" :
      "bg-red-100 text-red-800"
    }`}>
      {result.verdict === "pass" ? <CheckCircle2 className="w-3 h-3" /> :
       result.verdict === "fail" ? <XCircle className="w-3 h-3" /> :
       <AlertTriangle className="w-3 h-3" />}
      {result.verdict.toUpperCase()}
    </span>
  );

  return (
    <div className="space-y-3 text-xs">
      <div className="text-slate-600">
        Upload the Laaffic Excel export for a specific date. We&apos;ll pull the same day from{" "}
        <b>every active Laaffic team</b>, merge the results, and compare row-by-row on{" "}
        <code className="px-1 py-0.5 bg-slate-100 rounded">Call Center ID</code>.
        One Excel file covers all teams — no team dropdown needed.
      </div>

      <form onSubmit={submit} className="glass-card p-3 space-y-3" data-testid="sanity-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Date (must match Excel)</label>
            <input type="date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })}
                   className="glass-input w-full h-9 mt-1" data-testid="sanity-date" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Laaffic Excel export *</label>
            <input type="file" accept=".xlsx,.xls"
                   onChange={(e) => setFile(e.target.files?.[0] || null)}
                   className="glass-input w-full h-9 mt-1 text-[10px]" data-testid="sanity-file" />
          </div>
        </div>
        <Button type="submit" disabled={running || !file} data-testid="sanity-run">
          {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Comparing…</>
                   : <><Upload className="w-4 h-4 mr-2" /> Run sanity check</>}
        </Button>
      </form>

      {err && (
        <div className="glass-card p-3 text-red-700 border-l-4 border-red-500" data-testid="sanity-err">{err}</div>
      )}

      {(running || logs.length > 0) && (
        <div className="glass-card p-0 overflow-hidden" data-testid="sanity-logs">
          <div className="bg-slate-900 text-slate-100 text-[10px] font-mono p-2 max-h-64 overflow-auto"
               ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}>
            {logs.length === 0 && running && <div className="text-slate-400">Waiting for backend…</div>}
            {logs.map((l, i) => (
              <div key={i} className="whitespace-pre-wrap leading-snug">{l}</div>
            ))}
            {running && <div className="text-emerald-400 animate-pulse">▍</div>}
          </div>
        </div>
      )}

      {result && (
        <div className="space-y-3" data-testid="sanity-result">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="font-semibold text-slate-900">Verdict:</div>
            {verdictBadge}
            <Button size="sm" variant="outline" onClick={() => _downloadLaafficBundle(result, file?.name)}
                    data-testid="sanity-download">
              Download JSON bundle
            </Button>
            <Button size="sm" onClick={() => _downloadSanityPdf(result, file?.name)}
                    data-testid="sanity-download-pdf">
              Download PDF report
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <SBox label="Excel rows" value={result.excel.row_count} />
            <SBox label="API rows (all teams)" value={result.api.row_count} tone={
              result.api.row_count === result.excel.row_count ? "ok" : "warn"
            } />
            <SBox label="Matched on ID" value={result.diff.matched_count} tone="ok" />
            <SBox label="Tag mismatches" value={result.diff.tag_mismatches.length}
                  tone={result.diff.tag_mismatches.length === 0 ? "ok" : "warn"} />
          </div>

          {result.teams_pulled?.length > 0 && (
            <div className="glass-card p-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                Teams pulled ({result.teams_pulled.length})
              </div>
              <div className="flex flex-wrap gap-2 text-[11px]">
                {result.teams_pulled.map((t) => (
                  <span key={t.team_id}
                        className={`px-2 py-0.5 rounded-full font-mono ${t.error ? "bg-red-100 text-red-800" : "bg-slate-100"}`}>
                    {t.team_id.slice(0, 8)}: <b className="tabular-nums">{(t.pulled ?? 0).toLocaleString()}</b>
                    {t.error && <span className="ml-1">⚠</span>}
                  </span>
                ))}
              </div>
            </div>
          )}

          {result.api.fetch_error && (
            <div className="glass-card p-2 bg-amber-50 border-l-4 border-amber-400 text-amber-800 text-[11px]">
              API fetch error: {result.api.fetch_error}
            </div>
          )}

          {(result.diff.in_excel_not_api_count > 0 || result.diff.in_api_not_excel_count > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <details className="glass-card p-2">
                <summary className="cursor-pointer text-amber-800">
                  {result.diff.in_excel_not_api_count} in Excel but not API
                </summary>
                <pre className="mt-1 text-[10px] max-h-40 overflow-auto">{JSON.stringify(result.diff.in_excel_not_api, null, 2)}</pre>
              </details>
              <details className="glass-card p-2">
                <summary className="cursor-pointer text-amber-800">
                  {result.diff.in_api_not_excel_count} in API but not Excel
                </summary>
                <pre className="mt-1 text-[10px] max-h-40 overflow-auto">{JSON.stringify(result.diff.in_api_not_excel, null, 2)}</pre>
              </details>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Excel tag distribution</div>
              <TagCountsTable counts={result.excel.tag_counts} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">API tag distribution</div>
              <TagCountsTable counts={result.api.tag_counts} />
            </div>
          </div>

          {result.diff.tag_mismatches.length > 0 && (
            <details className="glass-card p-2">
              <summary className="cursor-pointer text-amber-800">
                {result.diff.tag_mismatches.length} tag mismatches (Excel tag ≠ API tag on same callSessionId)
              </summary>
              <pre className="mt-1 text-[10px] max-h-40 overflow-auto">{JSON.stringify(result.diff.tag_mismatches, null, 2)}</pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function SBox({ label, value, tone }) {
  const cls = tone === "ok" ? "text-emerald-700"
            : tone === "warn" ? "text-amber-700" : "text-slate-900";
  return (
    <div className="glass-card p-2">
      <div className="text-[9px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${cls}`}>{(value ?? 0).toLocaleString()}</div>
    </div>
  );
}

/** Build a self-contained JSON bundle the operator can email to
 *  Laaffic support: request template, response samples, tag
 *  mismatch pairs (Excel row + API record side-by-side), and the
 *  cases where a callSessionId appears in one source but not the
 *  other. Two samples per mismatch combination. */
function _downloadLaafficBundle(result, excelFileName) {
  const payload = {
    exported_at: new Date().toISOString(),
    excel_file: excelFileName || "(unknown)",
    date: result.date,
    verdict: result.verdict,
    summary: {
      excel_rows: result.excel?.row_count,
      api_rows: result.api?.row_count,
      matched: result.diff?.matched_count,
      missing_in_api: result.diff?.in_excel_not_api_count,
      extra_in_api: result.diff?.in_api_not_excel_count,
      tag_mismatches: result.diff?.tag_mismatches?.length || 0,
    },
    teams_pulled: result.teams_pulled,
    laaffic_request_template: result.laaffic_request,
    api_fetch_error: result.api?.fetch_error,
    excel_tag_distribution: result.excel?.tag_counts,
    api_tag_distribution: result.api?.tag_counts,
    tag_mismatch_combos_frequency: result.diff?.tag_mismatch_combos,
    tag_mismatch_samples: result.diff?.tag_mismatches,
    missing_in_api_samples: result.diff?.in_excel_not_api_samples,
    extra_in_api_samples: result.diff?.in_api_not_excel_samples,
    note_for_laaffic: (
      "Each entry under `tag_mismatch_samples` is a Call Center ID where the " +
      "Laaffic Excel export and the Laaffic API return different tags for the " +
      "SAME record. The `excel_row` and `api_record` blocks are the verbatim " +
      "data from each source. The API request template above shows the exact " +
      "endpoint + body we call (authentication headers are per-team-scoped)."
    ),
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `laaffic_support_bundle_${result.date || "sanity"}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Generate a printable PDF report suitable for forwarding to
 *  the Laaffic support team. Layout:
 *    - Header with date + verdict
 *    - Summary metrics table
 *    - Per-team pull table
 *    - Tag-mismatch pattern frequency table
 *    - Up to 20 sample mismatched rows (Excel-tag vs API-tag)
 *    - Missing-in-API + Extra-in-API sample tables
 *    - Full request template (endpoint, headers, body)
 */
function _downloadSanityPdf(result, excelFileName) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();

  // Header
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Laaffic sanity-check report", 40, 45);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(80);
  doc.text(`Date audited: ${result.date}`, 40, 62);
  doc.text(`Excel file:   ${excelFileName || "(unknown)"}`, 40, 76);
  doc.text(`Generated:    ${new Date().toISOString()}`, 40, 90);

  const verdict = (result.verdict || "").toUpperCase();
  const verdictColor = verdict === "PASS" ? [16, 185, 129]
                      : verdict === "FAIL" ? [220, 38, 38]
                      : [217, 119, 6];
  doc.setFillColor(...verdictColor);
  doc.setTextColor(255);
  doc.roundedRect(W - 130, 30, 90, 26, 6, 6, "F");
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(verdict || "—", W - 85, 48, { align: "center" });
  doc.setTextColor(0);

  // Summary
  autoTable(doc, {
    startY: 110,
    head: [["Metric", "Value"]],
    body: [
      ["Excel rows", (result.excel?.row_count || 0).toLocaleString()],
      ["Excel unique Call Center IDs", (result.excel?.unique_call_ids || 0).toLocaleString()],
      ["API rows (all teams merged)", (result.api?.row_count || 0).toLocaleString()],
      ["API unique Call Center IDs", (result.api?.unique_call_ids || 0).toLocaleString()],
      ["Matched on Call Center ID", (result.diff?.matched_count || 0).toLocaleString()],
      ["In Excel but missing from API", (result.diff?.in_excel_not_api_count || 0).toLocaleString()],
      ["In API but missing from Excel", (result.diff?.in_api_not_excel_count || 0).toLocaleString()],
      ["Tag mismatches (distinct pattern count)", Object.keys(result.diff?.tag_mismatch_combos || {}).length.toLocaleString()],
      ["Tag mismatches (total row count)", Object.values(result.diff?.tag_mismatch_combos || {}).reduce((a, b) => a + b, 0).toLocaleString()],
    ],
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [51, 65, 85], textColor: 255 },
    columnStyles: { 1: { halign: "right" } },
    margin: { left: 40, right: 40 },
  });

  if (result.teams_pulled?.length) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 14,
      head: [["Team ID", "Pulled", "Laaffic total", "Error"]],
      body: result.teams_pulled.map((t) => [
        t.team_id.slice(0, 12) + "…",
        (t.pulled || 0).toLocaleString(),
        (t.remote_total || 0).toLocaleString(),
        t.error || "—",
      ]),
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [51, 65, 85], textColor: 255 },
      margin: { left: 40, right: 40 },
    });
  }

  // Tag-mismatch pattern frequency
  const combos = Object.entries(result.diff?.tag_mismatch_combos || {}).sort((a, b) => b[1] - a[1]);
  if (combos.length) {
    doc.addPage();
    doc.setFontSize(14); doc.setFont("helvetica", "bold");
    doc.text("Tag-mismatch patterns", 40, 40);
    doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(80);
    doc.text(
      `Rows where the tag in the Excel export doesn't match the tag from the Laaffic API for the SAME Call Center ID.`,
      40, 56, { maxWidth: W - 80 }
    );
    doc.setTextColor(0);
    autoTable(doc, {
      startY: 76,
      head: [["Excel tag → API tag", "Count"]],
      body: combos.map(([k, v]) => [k, v.toLocaleString()]),
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [217, 119, 6], textColor: 255 },
      columnStyles: { 1: { halign: "right" } },
      margin: { left: 40, right: 40 },
    });
  }

  // Sample mismatched rows (up to 20)
  const samples = result.diff?.tag_mismatches || [];
  if (samples.length) {
    doc.addPage();
    doc.setFontSize(14); doc.setFont("helvetica", "bold");
    doc.text(`Sample mismatched rows (${samples.length})`, 40, 40);
    doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(80);
    doc.text(
      "Each row: Call Center ID, the tag Laaffic returned in the Excel export, and the tag the Laaffic API returned for the same record.",
      40, 56, { maxWidth: W - 80 }
    );
    doc.setTextColor(0);
    autoTable(doc, {
      startY: 76,
      head: [["Call Center ID", "Excel tag", "API tag", "sipGroup / start"]],
      body: samples.map((s) => [
        s.callSessionId,
        String(s.excel_tag ?? "—"),
        String(Array.isArray(s.api_tag) ? s.api_tag.join(", ") : (s.api_tag ?? "—")),
        `${s.api_record?.sipGroupName || "—"}\n${s.api_record?.callStartTime || ""}`,
      ]),
      styles: { fontSize: 8, cellPadding: 3, valign: "top" },
      headStyles: { fillColor: [217, 119, 6], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 130 }, 1: { cellWidth: 90 }, 2: { cellWidth: 120 }, 3: { cellWidth: 175 },
      },
      margin: { left: 40, right: 40 },
    });
  }

  // Missing / Extra
  const missing = result.diff?.in_excel_not_api_samples || [];
  const extra = result.diff?.in_api_not_excel_samples || [];
  if (missing.length || extra.length) {
    doc.addPage();
    doc.setFontSize(14); doc.setFont("helvetica", "bold");
    doc.text("Rows appearing in one source only", 40, 40);
    let y = 60;
    if (missing.length) {
      doc.setFontSize(11);
      doc.text(`In Excel but not in API (${result.diff.in_excel_not_api_count || 0} total, showing ${missing.length})`, 40, y);
      autoTable(doc, {
        startY: y + 8,
        head: [["Call Center ID", "Excel: Agent Group", "Start Time", "Tags"]],
        body: missing.map((s) => [
          s.callSessionId,
          s.excel_row?.["Agent Group"] || "—",
          s.excel_row?.["Call Start Time"] || "—",
          s.excel_row?.["Tags"] || "—",
        ]),
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [220, 38, 38], textColor: 255 },
        margin: { left: 40, right: 40 },
      });
      y = doc.lastAutoTable.finalY + 16;
    }
    if (extra.length) {
      doc.setFontSize(11);
      doc.text(`In API but not in Excel (${result.diff.in_api_not_excel_count || 0} total, showing ${extra.length})`, 40, y);
      autoTable(doc, {
        startY: y + 8,
        head: [["Call Center ID", "sipGroupName", "callStartTime", "labelNameList"]],
        body: extra.map((s) => [
          s.callSessionId,
          s.api_record?.sipGroupName || "—",
          s.api_record?.callStartTime || "—",
          Array.isArray(s.api_record?.labelNameList) ? s.api_record.labelNameList.join(", ") : "—",
        ]),
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [220, 38, 38], textColor: 255 },
        margin: { left: 40, right: 40 },
      });
    }
  }

  // Request evidence
  if (result.laaffic_request) {
    doc.addPage();
    doc.setFontSize(14); doc.setFont("helvetica", "bold");
    doc.text("How we called the Laaffic API", 40, 40);
    doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(80);
    doc.text("For each Laaffic team credential on file, we make this request paginated (currentPage 1..N):", 40, 60, { maxWidth: W - 80 });
    doc.setTextColor(0);
    doc.setFontSize(9); doc.setFont("courier", "normal");
    const lines = [
      `${result.laaffic_request.method} ${result.laaffic_request.url}`,
      "",
      "Headers:",
      ...(result.laaffic_request.auth_headers || []).map((h) => "  " + h),
      "",
      "Body (JSON):",
      ...JSON.stringify(result.laaffic_request.body_sample, null, 2).split("\n").map((l) => "  " + l),
    ];
    let y = 84;
    for (const line of lines) {
      if (y > 780) { doc.addPage(); y = 40; }
      doc.text(line, 40, y);
      y += 12;
    }
  }

  // Footer on every page
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(140);
    doc.text(`Laaffic sanity report — ${result.date} — page ${i} of ${pageCount}`,
              W / 2, doc.internal.pageSize.getHeight() - 20, { align: "center" });
  }

  doc.save(`laaffic_sanity_${result.date}.pdf`);
}



function TagCountsTable({ counts }) {
  const entries = Object.entries(counts || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="glass-card p-0 overflow-hidden">
      <table className="w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500 text-left">
          <tr><th className="px-2 py-1">Tag (lower)</th><th className="px-2 py-1 text-right">Count</th></tr>
        </thead>
        <tbody>
          {entries.map(([tag, cnt]) => (
            <tr key={tag} className="border-t border-slate-100">
              <td className="px-2 py-1 font-mono">{tag}</td>
              <td className="px-2 py-1 text-right tabular-nums">{cnt}</td>
            </tr>
          ))}
          {entries.length === 0 && (
            <tr><td colSpan={2} className="px-2 py-2 text-slate-400 text-center">no data</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
